import { useMemo } from 'react';
import { ChevronRightIcon, HelicopterIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/base-components';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import {
  useGetTransportsQuery,
  type EtlTransportResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  BannerWrapper,
  IconWrapper,
  IconWrapperChevron,
} from '../../../../AirAmbulanceForm/Banner/StatusBanner.styles';
import {
  formatTransportStatuses,
  getActiveDispatchStatusDisplay,
  getLatestTransportActivityMs,
  getResourceRequestQueueCategory,
  getStatusPageState,
} from '../../../../AirAmbulanceForm/etl/etlStatusUtils';
import AirAmbulanceView from '../ResourceRequestPanel/AirAmbulanceView';
import TransportListItem from '../ResourceRequestPanel/TransportListItem';
import type {
  ResourceRequestAdapter,
  ResourceRequestBannerProps,
  ResourceRequestIngestionMetadata,
  ResourceRequestListItemProps,
  ResourceRequestPickupLocation,
} from './types';

/**
 * Resolve pickup coordinates for map panning from the backend-composited
 * `resolved_pickup_location`, which already merges the order with every status
 * update (GMR can send pickup changes on status rows without refreshing the
 * transport's verbatim_message).
 */
function getAirAmbulancePickupLocation(
  transport: EtlTransportResponse,
): ResourceRequestPickupLocation | null {
  const { latitude, longitude } = transport.resolved_pickup_location ?? {};

  if (latitude == null || longitude == null) {
    return null;
  }

  return { latitude, longitude };
}

function useAirAmbulanceResourceList({ skip }: { skip: boolean }): {
  requests: EtlTransportResponse[];
  isFetching: boolean;
} {
  const { data, isFetching } = useGetTransportsQuery(undefined, {
    skip,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const requests = useMemo(() => data?.transports ?? [], [data]);
  return { requests, isFetching };
}

function AirAmbulanceBanner({
  request,
  onOpen,
}: ResourceRequestBannerProps<EtlTransportResponse>): JSX.Element {
  const formattedStatuses = formatTransportStatuses(request.statuses);
  const statusPageState = getStatusPageState(request, formattedStatuses);
  const IconComponent = statusPageState.icon;
  const RESOURCE_REQUEST_TYPE_LABEL = 'Air ambulance';
  const latestStatusDisplay = getActiveDispatchStatusDisplay(request);
  const displayText = latestStatusDisplay
    ? `${RESOURCE_REQUEST_TYPE_LABEL} status: ${latestStatusDisplay}`
    : statusPageState.message;

  return (
    <BannerWrapper bgColor={statusPageState.bgColor} horizontal>
      <IconWrapper>
        <IconComponent color="background" height={16} width={16} />
      </IconWrapper>
      <Text color="background" ml="12px" size="normal">
        {displayText}
      </Text>
      <IconWrapperChevron onClick={onOpen}>
        <ChevronRightIcon
          height={10}
          width={10}
          viewboxWidth={16}
          viewboxHeight={16}
          data-name="resource-request-banner-chevron"
        />
      </IconWrapperChevron>
    </BannerWrapper>
  );
}

/**
 * Renders a list item for an Air Ambulance resource request.
 * @param param0
 * @returns
 */
function AirAmbulanceListItem({
  request,
  isUnread,
  onClick,
}: ResourceRequestListItemProps<EtlTransportResponse>): JSX.Element {
  return (
    <TransportListItem
      transport={request}
      isUnread={isUnread}
      onClick={onClick}
    />
  );
}

function getAirAmbulanceIngestionMetadata(
  transport: EtlTransportResponse,
): ResourceRequestIngestionMetadata {
  const method = transport.verbatim_message?.requestMethod;
  return {
    ingestionSource: transport.ingestion_source ?? null,
    requestMethod: typeof method === 'string' ? method : null,
  };
}

export const airAmbulanceAdapter: ResourceRequestAdapter<EtlTransportResponse> =
  {
    id: 'air_ambulance',
    title: 'Air Ambulance',
    source: 'GMR',
    description:
      'Request emergency helicopter services for severe medical emergencies',
    Icon: HelicopterIcon,
    useEnabled: useHasAccessToAirAmbulance,
    useList: useAirAmbulanceResourceList,
    getId: transport => transport.gmr_transport_id,
    getCallerId: transport => transport.caller_id,
    getQueueInfo: (transport, nowMs) => {
      const statuses = formatTransportStatuses(transport.statuses);
      return {
        category: getResourceRequestQueueCategory(transport, statuses, nowMs),
        activityMs: getLatestTransportActivityMs(transport, statuses),
      };
    },
    getPickupLocation: getAirAmbulancePickupLocation,
    getIngestionMetadata: getAirAmbulanceIngestionMetadata,
    ListItemComponent: AirAmbulanceListItem,
    ViewComponent: AirAmbulanceView,
    BannerComponent: AirAmbulanceBanner,
  };
